import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import EmailTemplateService from '../../utils/emailTemplateService';
import TemplateEditor from './components/TemplateEditor';
import TemplatePreview from './components/TemplatePreview';
import TemplateList from './components/TemplateList';

const AdminEmailTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'edit', 'preview'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [emailStats, setEmailStats] = useState(null);
  const [notification, setNotification] = useState(null);

  // Load templates and stats on component mount
  useEffect(() => {
    loadTemplates();
    loadEmailStats();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await EmailTemplateService.getAllTemplates();
      if (result.success) {
        setTemplates(result.data);
      } else {
        showNotification('Error loading templates: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error loading templates: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailStats = async () => {
    try {
      const result = await EmailTemplateService.getEmailStats(30);
      if (result.success) {
        setEmailStats(result.data);
      }
    } catch (error) {
      console.error('Error loading email stats:', error);
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setActiveTab('edit');
  };

  const handleTemplateUpdate = async (templateType, updates) => {
    setSaving(true);
    try {
      const result = await EmailTemplateService.updateTemplate(templateType, updates);
      if (result.success) {
        showNotification('Template updated successfully!', 'success');
        await loadTemplates();
        // Update selected template
        setSelectedTemplate(result.data);
      } else {
        showNotification('Error updating template: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error updating template: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateToggle = async (templateType, isActive) => {
    try {
      const result = await EmailTemplateService.toggleTemplateStatus(templateType, isActive);
      if (result.success) {
        showNotification(`Template ${isActive ? 'activated' : 'deactivated'} successfully!`, 'success');
        await loadTemplates();
      } else {
        showNotification('Error updating template status: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error updating template status: ' + error.message, 'error');
    }
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setActiveTab('preview');
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.template_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && template.is_active) ||
                         (filterStatus === 'inactive' && !template.is_active);
    return matchesSearch && matchesStatus;
  });

  const tabs = [
    { id: 'list', label: 'Templates', icon: 'List' },
    { id: 'edit', label: 'Editor', icon: 'Edit', disabled: !selectedTemplate },
    { id: 'preview', label: 'Preview', icon: 'Eye', disabled: !selectedTemplate }
  ];

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      
      <main className="lg:pl-72">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Email Templates</h1>
                <p className="text-muted-foreground mt-2">
                  Manage and customize email templates sent to users
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  onClick={loadTemplates}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  iconName="Plus"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setActiveTab('edit');
                  }}
                >
                  New Template
                </Button>
              </div>
            </div>

            {/* Email Stats */}
            {emailStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Sent</p>
                      <p className="text-2xl font-bold text-foreground">
                        {emailStats.total_sent?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <Icon name="Send" className="text-primary" size={24} />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Rate</p>
                      <p className="text-2xl font-bold text-foreground">
                        {emailStats.delivery_rate || '0'}%
                      </p>
                    </div>
                    <Icon name="CheckCircle" className="text-accent" size={24} />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Open Rate</p>
                      <p className="text-2xl font-bold text-foreground">
                        {emailStats.open_rate || '0'}%
                      </p>
                    </div>
                    <Icon name="Mail" className="text-secondary" size={24} />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Click Rate</p>
                      <p className="text-2xl font-bold text-foreground">
                        {emailStats.click_rate || '0'}%
                      </p>
                    </div>
                    <Icon name="MousePointer" className="text-primary" size={24} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notification */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg border ${
              notification.type === 'success' ? 'bg-accent/10 border-accent text-accent' :
              notification.type === 'error' ? 'bg-destructive/10 border-destructive text-destructive' :
              'bg-primary/10 border-primary text-primary'
            }`}>
              <div className="flex items-center space-x-2">
                <Icon 
                  name={notification.type === 'success' ? 'CheckCircle' : 
                        notification.type === 'error' ? 'AlertCircle' : 'Info'} 
                  size={20} 
                />
                <span>{notification.message}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setNotification(null)}
                  className="ml-auto"
                />
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && setActiveTab(tab.id)}
                  disabled={tab.disabled}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : tab.disabled
                      ? 'border-transparent text-muted-foreground/50 cursor-not-allowed'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'list' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search templates..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      iconName="Search"
                    />
                  </div>
                  <div className="sm:w-48">
                    <Select
                      value={filterStatus}
                      onChange={(value) => setFilterStatus(value)}
                      options={[
                        { value: 'all', label: 'All Templates' },
                        { value: 'active', label: 'Active Only' },
                        { value: 'inactive', label: 'Inactive Only' }
                      ]}
                    />
                  </div>
                </div>

                {/* Template List */}
                <TemplateList
                  templates={filteredTemplates}
                  loading={loading}
                  onTemplateSelect={handleTemplateSelect}
                  onTemplateToggle={handleTemplateToggle}
                  onTemplatePreview={handlePreviewTemplate}
                />
              </div>
            )}

            {activeTab === 'edit' && (
              <TemplateEditor
                template={selectedTemplate}
                onSave={handleTemplateUpdate}
                saving={saving}
                onCancel={() => {
                  setSelectedTemplate(null);
                  setActiveTab('list');
                }}
              />
            )}

            {activeTab === 'preview' && selectedTemplate && (
              <TemplatePreview
                template={selectedTemplate}
                onBack={() => setActiveTab('list')}
                onEdit={() => setActiveTab('edit')}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEmailTemplates;
